import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar } from '../../../components/Calendar';
import { FacilityService } from '../../../services/FacilityService';
import { ReservationService } from '../../../services/ReservationService';
import { Facility } from '../../../models/Facility';
import { FacilityReservation } from '../../../models/FacilityReservation';
import { useAdminAuth } from '../../../hooks/admin/useAdminAuth';
import { useToast } from '../../../hooks/useToast';

// 상수 정의
const PAGE_TITLE = '시설물 예약 캘린더';
const INFO_MESSAGE = '시설물별 예약 현황을 캘린더 형태로 확인할 수 있습니다. 시설물을 선택하여 해당 시설의 예약 현황을 조회하세요.';
const SELECT_FACILITY_LABEL = '시설물 선택';
const SELECT_FACILITY_PLACEHOLDER = '시설물을 선택하세요';
const CALENDAR_TITLE = '예약 캘린더';
const BACK_BUTTON_TEXT = '목록으로';

const FacilityReservationsCalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, userLevel, permissions, loading: authLoading } = useAdminAuth();
  const { showToast } = useToast();
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [reservations, setReservations] = useState<FacilityReservation[]>([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // 페이지 진입 시 스크롤을 최상단으로 이동
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 에러 처리 유틸리티 함수
  const getErrorMessage = useCallback((error: unknown): string => {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    return '알 수 없는 오류가 발생했습니다.';
  }, []);

  // 데이터 검증 함수
  const validateData = useCallback((facilities: unknown, reservations: unknown): boolean => {
    if (!Array.isArray(facilities) || !Array.isArray(reservations)) {
      return false;
    }
    return true;
  }, []);

  // 데이터 로딩 함수 (메모이제이션)
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [fetchedFacilities, fetchedReservations] = await Promise.all([
        FacilityService.getAllFacilities(),
        ReservationService.getAllReservations()
      ]);

      // 데이터 검증
      if (!validateData(fetchedFacilities, fetchedReservations)) {
        throw new Error('서버에서 잘못된 형태의 데이터를 받았습니다.');
      }

      setFacilities(fetchedFacilities);
      setReservations(fetchedReservations);
      setIsInitialized(true);

      if (!isInitialized) {
        showToast('시설물 및 예약 데이터를 성공적으로 불러왔습니다.', { type: 'success' });
      }
    } catch (error: unknown) {
      console.error('데이터 조회 실패:', error);
      const errorMsg = getErrorMessage(error);
      setError(errorMsg);
      showToast(errorMsg, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [showToast, validateData, getErrorMessage, isInitialized]);

  // 시설물 선택 핸들러 (메모이제이션)
  const handleFacilityChange = useCallback((facilityId: string) => {
    try {
      const validFacilityId = facilityId.trim() || null;
      setSelectedFacilityId(validFacilityId);

      if (validFacilityId) {
        const selectedFacility = facilities.find(f => f?.id === validFacilityId);
        if (selectedFacility?.name) {
          showToast(`${selectedFacility.name} 시설물이 선택되었습니다.`, { type: 'info' });
        } else {
          showToast('선택한 시설물 정보를 찾을 수 없습니다.', { type: 'warning' });
          setSelectedFacilityId(null);
        }
      }
    } catch (error: unknown) {
      console.error('시설물 선택 중 오류:', error);
      showToast('시설물 선택 중 오류가 발생했습니다.', { type: 'error' });
      setSelectedFacilityId(null);
    }
  }, [facilities, showToast]);

  // 필터링된 예약 데이터 메모이제이션
  const filteredReservations = useMemo(() => {
    if (!selectedFacilityId) {
      return reservations;
    }
    return reservations.filter(reservation => reservation.facilityId === selectedFacilityId);
  }, [reservations, selectedFacilityId]);

  // 권한 검사 및 데이터 로딩
  useEffect(() => {
    if (!authLoading) {
      // 최고관리자이거나 시설물 예약 관리 권한이 있는 경우에만 접근 가능
      if (!user || (userLevel !== 1 && !permissions?.facilityReservations)) {
        showToast('접근 권한이 없습니다.', { type: 'error' });
        navigate('/admin/dashboard');
        return;
      }

      fetchData();
    }
  }, [user, userLevel, permissions, authLoading, navigate, fetchData, showToast]);

  // 컴포넌트 언마운트 시 정리 작업
  useEffect(() => {
    return () => {
      // 메모리 누수 방지를 위한 상태 초기화
      setFacilities([]);
      setReservations([]);
      setSelectedFacilityId(null);
      setError(null);
      setIsInitialized(false);
    };
  }, []);

  if (loading) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#155acb]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-[#f7f9fd]">
      {/* 본문 컨테이너 - 중앙 정렬 */}
      <div className="max-w-[1200px] mx-auto relative pt-8 px-4">
        {/* 헤더 섹션 */}
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <div className="text-slate-800 text-4xl font-extrabold font-['Pretendard'] leading-9">
              {PAGE_TITLE}
            </div>
            <button
              onClick={() => navigate('/admin/facility-reservations')}
              className="w-[130px] h-11 bg-[#6b7684] text-white text-base font-bold font-['Pretendard'] rounded-sm hover:bg-[#6b7684]/90 transition-colors"
            >
              {BACK_BUTTON_TEXT}
            </button>
          </div>
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
        </div>

        {/* 안내 메시지 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-6 bg-[#f2f6fb] border-l-4 border-[#155acb] rounded-r-sm">
            <p className="text-[#1e2c49] text-lg font-medium font-['Pretendard']">
              {INFO_MESSAGE}
            </p>
          </div>
        </div>

        {/* 시설물 선택 필터 카드 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-8">
            <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
              {SELECT_FACILITY_LABEL}
            </h2>
            <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>

            <div>
              <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">
                {SELECT_FACILITY_LABEL}
              </label>
              <select
                className="w-full md:w-96 rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                value={selectedFacilityId || ''}
                onChange={(e) => handleFacilityChange(e.target.value)}
              >
                <option value="">{SELECT_FACILITY_PLACEHOLDER}</option>
                {facilities.map((facility) => (
                  <option key={facility.id} value={facility.id}>
                    {facility.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* 캘린더 카드 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-8">
            <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
              {CALENDAR_TITLE}
            </h2>
            <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>
            
            <Calendar
              reservations={filteredReservations}
              facilities={facilities}
              selectedFacilityId={selectedFacilityId}
            />
          </div>
        </div>

        {/* 하단 여백 */}
        <div className="h-16"></div>
      </div>
    </div>
  );
};

export default FacilityReservationsCalendarPage; 